import { gql } from "lib/apolloClient";

/**
 * Single source of truth for assessment learning goals fragments.
 * Used by both AssessmentLearningGoalsFragments.js (new queries) and
 * assessmentResolvedFieldValueFragment (assessment detail queries).
 *
 * These are superset definitions containing ALL fields needed by any consumer.
 */
export const assessmentPlannerElementFragments = {
  lp: gql`
    fragment lpItem on PlannerLearnerProfile {
      id
      label
      subText
      levelId
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  concept: gql`
    fragment conceptItem on PlannerConcept {
      id
      label
      subText
      levelId
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  relatedConcept: gql`
    fragment relatedConceptItem on PlannerRelatedConcept {
      id
      label
      elementId
      elementType
      parent
      children
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  action: gql`
    fragment actionItem on Action {
      id
      label
      subText
      children
      parent
      levels {
        id
        value
      }
      levelId
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  theme: gql`
    fragment themeItem on PlannerTheme {
      id
      label
      descriptor
      metadata
      associatedChildren {
        id
        label
      }
      focusAreas {
        id
        label
      }
      icon
    }
  `,
  atl: gql`
    fragment atlItem on PlannerATL {
      id
      label
      children
      parent
      levelId
      levels {
        id
        value
      }
      depth
      isLeaf
      displaySequence
      atlCategory {
        id
      }
      subText
      grades {
        id
        name
      }
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
    }
  `,
  plannerElementNode: gql`
    fragment plannerElementNodeItem on PlannerElementNode {
      id
      displaySequence
      label
      subText
      code
      type
      depth
      children
      parent
      subType
      isLeaf
      metadata
      updatedAt
      tags: genericTags {
        id
        label
        icon {
          id
          url
        }
      }
      dataSetNodeWrapper {
        properties {
          isGraded
          grades {
            id
            name
          }
          subjects {
            id
            name
            isArchived
          }
          subjectGroups {
            id
            name
            constants
          }
          associatedPlannerElementNodes {
            id
            label
            type
            metadata
          }
        }
      }
      gradingType
      standardsWrapper {
        standardSet {
          id
          title
        }
        properties {
          isGradedStandard
          tags {
            id
            label
          }
        }
      }
      plannerElementNodeWrapper {
        properties {
          isGradedStandard
        }
      }
      parentData {
        id
      }
    }
  `,
};

export const assessmentBenchmarkFragment = {
  benchmark: gql`
    fragment benchmarkItem on PlannerBenchmark {
      id
      label
      subText
      children
      parent
      subject
      levelId
      displaySequence
      depth
      type
      code
      grades {
        id
        name
      }
      isLeaf
      tags {
        id
        label
        icon {
          id
          url
        }
      }
      dataSetNodeWrapper {
        properties {
          isGraded
        }
      }
      gradingType
      standardsWrapper {
        standardSet {
          id
          title
        }
        properties {
          isGradedStandard
          tags {
            id
            label
          }
        }
      }
      parentData {
        id
      }
    }
  `,
};

export const plannerElementNodeSetFragment = {
  plannerElementNodeSet: gql`
    fragment plannerElementNodeSetItem on PlannerElementNodeSetConnection {
      edges {
        node {
          id
          type
          title
          plannerElementSet {
            id
            type
          }
        }
      }
    }
  `,
  plannerElementNodeSetWithNodes: gql`
    fragment plannerElementNodeSetWithNodesItem on PlannerElementNodeSet {
      id
      title
      type
      plannerElementSet {
        id
        type
        label
        config {
          id
          config
          valueType
          value
        }
      }
      plannerElementNodeSetWrapperV2 {
        ... on PlannerElementNodeSetWrapper {
          publishedData(filters: $plannerElementNodeFilters) {
            edges {
              node {
                ...plannerElementNodeItem
              }
            }
          }
        }
        ... on PlannerActionSetWrapper {
          publishedData(filters: $plannerElementNodeFilters) {
            edges {
              node {
                ...actionItem
              }
            }
          }
        }
        ... on PlannerLearnerProfileSetWrapper {
          publishedData(filters: $plannerElementNodeFilters) {
            edges {
              node {
                ...lpItem
              }
            }
          }
        }
        ... on PlannerConceptSetWrapper {
          publishedData(filters: $plannerElementNodeFilters) {
            edges {
              node {
                ...conceptItem
              }
            }
          }
        }
        ... on PlannerRelatedConceptSetWrapper {
          publishedData(filters: $plannerElementNodeFilters) {
            edges {
              node {
                ...relatedConceptItem
              }
            }
          }
        }
        ... on PlannerATLSetWrapper {
          publishedData(filters: $plannerElementNodeFilters) {
            edges {
              node {
                ...atlItem
              }
            }
          }
        }
      }
    }
    ${assessmentPlannerElementFragments.plannerElementNode}
    ${assessmentPlannerElementFragments.action}
    ${assessmentPlannerElementFragments.lp}
    ${assessmentPlannerElementFragments.concept}
    ${assessmentPlannerElementFragments.relatedConcept}
    ${assessmentPlannerElementFragments.atl}
  `,
};
